import { useEffect, useState } from "react";

export type Props = {
  /**
   * Render while the session is presumed healthy. Typically the
   * `<Chatbox>` (or whatever the surface puts in its place).
   */
  children: React.ReactNode;
  /**
   * Rendered in place of children once the timeout fires. Composed at
   * the platform-layer call site so this component stays free of theme
   * / UI imports — it lives in `core/`.
   *
   * The fallback is responsible for its own retry click handling. The
   * detector does NOT own the click; it reacts to the resulting
   * external state change via `resetSignal` (see below). This keeps
   * the detector decoupled from `chatSlice` and analytics.
   *
   * See docs/chat-stuck-spinner-fix-plan.md §5.1.
   */
  fallback: React.ReactNode;
  /**
   * Any value that, when changed, resets the detector to the children
   * branch and re-arms the timer. The platform-layer call site passes
   * `chatSlice.sessionRetryKey` here so that clicking Try Again bumps
   * the slice → resetSignal changes → detector flips back to children
   * → fresh timer starts → fresh Chatbox renders against the recycled
   * `Talk.Session`.
   *
   * Stable across re-renders within a single retry cycle. Type is
   * intentionally permissive — `number` is what the immediate call
   * sites pass (the slice counter). The `string` variant is a
   * forward-compat hatch for the conversation-switch fix in §9.5
   * option (a), where callers might pass
   * `${sessionRetryKey}:${conversationId}` to combine multiple reset
   * triggers.
   */
  resetSignal: number | string;
  /**
   * Called once per stuck cycle, the moment the timer fires and the
   * surface flips to fallback. Use to emit fleet telemetry. Optional;
   * no-op default. Dedupe per-session, if needed, in the analytics
   * layer (NOT here).
   */
  onStuck?: () => void;
  /** Milliseconds before declaring the surface stuck. Default 25_000. */
  timeoutMs?: number;
};

const DEFAULT_TIMEOUT_MS = 25_000;

/**
 * Detects when a child surface (typically `<Chatbox>` from
 * `@talkjs/react`) has been rendering for too long without recovering,
 * and swaps to a fallback (typically a "chat couldn't connect — try
 * again" UI). The default timeout is 25s; see
 * docs/chat-stuck-spinner-fix-plan.md §6.1 for the rationale.
 *
 * The detector's own state (`isStuck`) is reset by `resetSignal`
 * changing — the platform-layer call site bumps `chatSlice.sessionRetryKey`
 * on the user's retry click and forwards the new value here as
 * `resetSignal`. That same slice change drives `<ChatSession>`'s
 * `syncUser` memo to flip and recycle the underlying `Talk.Session`.
 * The two effects run in parallel and converge.
 *
 * Wrap ONLY the live Chatbox branch — never the picker / switcher /
 * empty-state surfaces. The timer is wall-clock and doesn't know what
 * it's wrapping; if there's no Chatbox, false positives.
 */
export function ChatboxStuckDetector({
  children,
  fallback,
  resetSignal,
  onStuck,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: Props) {
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    setIsStuck(false);
    const timer = setTimeout(() => {
      setIsStuck(true);
      onStuck?.();
    }, timeoutMs);
    return () => clearTimeout(timer);
    // onStuck deliberately omitted from deps — see fix plan §9.6.
    // Including it would re-arm the timer on every render when call
    // sites pass an inline arrow as onStuck (which they do, see §5.5).
    // The timer fires once per stuck cycle and calls whichever onStuck
    // reference is current at that moment, which is correct for
    // one-shot telemetry. If a future use case needs onStuck to be
    // dep-tracked, switch to a useRef-and-current pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal, timeoutMs]);

  return <>{isStuck ? fallback : children}</>;
}

export default ChatboxStuckDetector;
